// Ties every other sim module together into the two functions the server
// and the client both call each tick: stepCarOnTrack (one car's physics on
// its track, used both by the authoritative server loop and by client-side
// prediction replay) and stepRace (a full tick of a race: phase timers,
// every car's input and physics, car-to-car collisions, progress, camera,
// elimination and finish checks).

import type { Vec2 } from '../math/vec';
import { v } from '../math/vec';
import { wrapAngle } from '../math/geom';
import { makeRng } from '../math/rng';
import type { CarState, InputFrame } from './types';
import type { CarTuning } from './tuning';
import { AI_DIFFICULTY } from './tuning';
import { stepCar } from './car';
import type { PreparedTrack } from './track';
import { surfaceAt, rampAt, collideWalls, resolveWallHit } from './track';
import type { RaceState, RaceConfig } from './race';
import { advancePhase } from './race';
import { aiInput } from './ai';

export const DT = 1 / 60;

// A neutral input frame: no throttle, no brake, no steer. Used as the
// fallback for a human slot with no input queued for this tick, and as the
// base frame an eliminated car's physics coasts on.
export function zeroInput(seq: number): InputFrame {
  return { seq, throttle: 0, brake: 0, steer: 0 };
}

// One car's physics for one tick on its track: surface lookup, ramp launch
// (only while grounded, and only once the car is at or above the ramp's
// minSpeed), stepCar itself, then wall collision (only while grounded,
// since an airborne car flies over walls). This is THE single per-car step:
// the server calls it for every car every tick, and the client replays it
// during prediction reconciliation. Nothing else touches car state here;
// progress/lap bookkeeping is updateProgress's job (race.ts), called
// separately by stepRace.
export function stepCarOnTrack(track: PreparedTrack, car: CarState, input: InputFrame, tuning: CarTuning): void {
  const surface = surfaceAt(track, car.pos);

  if (car.airborne === 0) {
    const speed = v.len(car.vel);
    const ramp = rampAt(track, car.pos);
    if (ramp && speed >= ramp.minSpeed) {
      car.airborne = ramp.airTime;
    }
  }

  stepCar(car, input, tuning, surface.mod, DT);
  car.surface = surface.name;

  if (car.airborne === 0) {
    const push = collideWalls(track, car.pos, tuning.radius);
    if (push) resolveWallHit(car, push);
  }
}

// Decays an eliminated car to a stop instead of stepping it with input: it
// still integrates position from its current velocity, but drag is applied
// at 3x the tuning's normal rate (the 1x stepCar would have applied, plus
// an extra 2x here) so it visibly coasts to rest rather than sailing off
// under its pre-elimination speed. Also counts airborne down toward 0 like
// stepCar does, so a car eliminated mid-jump lands instead of reporting
// airborne > 0 (and rendering enlarged with an offset shadow) for the rest
// of the round.
export function stepEliminatedCar(car: CarState, tuning: CarTuning, dt: number): void {
  car.pos = v.add(car.pos, v.scale(car.vel, dt));
  const extraDragFactor = Math.max(0, 1 - 2 * tuning.drag * dt);
  car.vel = v.scale(car.vel, extraDragFactor * (1 - tuning.drag * dt));
  if (car.airborne > 0) {
    car.airborne = Math.max(0, car.airborne - dt);
  }
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

// Pushes two overlapping cars apart: half the overlap each along the line
// between their centres, a small heading nudge away from each other, and a
// 30% reduction of the velocity component that is closing the gap between
// them (velocity moving them apart is left untouched).
function pushApart(a: CarState, b: CarState, minDist: number): void {
  const delta = v.sub(b.pos, a.pos);
  const dist = v.len(delta);
  if (dist >= minDist) return;

  // Degenerate case: the two cars sit exactly on top of each other. Pick a
  // deterministic fallback axis (derived from the car ids) rather than
  // dividing by zero.
  const n = dist > 1e-9 ? v.scale(delta, 1 / dist) : v.fromAngle(a.id - b.id);

  const overlap = minDist - dist;
  const half = overlap / 2;
  a.pos = v.sub(a.pos, v.scale(n, half));
  b.pos = v.add(b.pos, v.scale(n, half));

  nudgeAway(a, n);
  nudgeAway(b, v.scale(n, -1));

  const relVel = v.sub(b.vel, a.vel);
  const vAlongN = v.dot(relVel, n);
  if (vAlongN < 0) {
    // Closing: reduce the magnitude of the along-n relative velocity by
    // 30%, split equally between both cars so the correction conserves
    // momentum.
    const deltaV = -0.3 * vAlongN;
    const correction = v.scale(n, deltaV / 2);
    b.vel = v.add(b.vel, correction);
    a.vel = v.sub(a.vel, correction);
  }
}

const COLLISION_HEADING_NUDGE = 0.08;

// Nudges car's heading by COLLISION_HEADING_NUDGE away from toward, the
// unit vector pointing from car toward the other car it just collided with.
function nudgeAway(car: CarState, toward: Vec2): void {
  const fwd = v.fromAngle(car.heading);
  const right = { x: -fwd.y, y: fwd.x };
  const side = v.dot(toward, right);
  car.heading = wrapAngle(car.heading + (side > 0 ? -COLLISION_HEADING_NUDGE : COLLISION_HEADING_NUDGE));
}

// One full tick of a race: phase timers, every car's input and physics
// (only while racing; frozen phases skip car stepping entirely), then the
// progress/camera/elimination/finish bookkeeping race.ts's advancePhase
// already knows how to run in order. state.tick is incremented exactly
// once per call, by advancePhase itself.
export function stepRace(
  track: PreparedTrack,
  state: RaceState,
  inputs: Record<number, InputFrame>,
  cfg: RaceConfig,
  tuning: CarTuning,
): void {
  if (state.phase === 'racing') {
    let bestHumanProgress: number | null = null;
    for (const slot of state.slots) {
      if (slot.kind !== 'human') continue;
      const car = state.cars.find((c) => c.id === slot.id);
      if (!car) continue;
      if (bestHumanProgress === null || car.progress > bestHumanProgress) {
        bestHumanProgress = car.progress;
      }
    }

    for (const slot of state.slots) {
      const car = state.cars.find((c) => c.id === slot.id);
      if (!car) continue;

      if (car.eliminated) {
        stepEliminatedCar(car, tuning, DT);
        continue;
      }

      let input: InputFrame;
      if (slot.kind === 'human') {
        input = inputs[slot.id] ?? zeroInput(0);
      } else {
        // Each AI car gets its own lane so a field of them does not stack on one line.
        const slotIndex = state.slots.indexOf(slot);
        const laneOffset = ((slotIndex % 3) - 1) * 1.1;
        const params = { ...AI_DIFFICULTY[slot.difficulty ?? 'normal'], laneOffset };
        const targetProgress = bestHumanProgress ?? car.progress;
        const rubberBand = 1 + clamp((targetProgress - car.progress) / 40, -0.08, 0.1);
        const rng = makeRng(state.seed ^ state.tick ^ (car.id * 7919));
        input = aiInput(track, state, car, params, rubberBand, rng, state.tick);
      }

      stepCarOnTrack(track, car, input, tuning);
    }

    // Eliminated cars are out of play and only coasting to a stop; they
    // must neither shove a live car off its line nor be shoved themselves,
    // so any pair involving one is skipped entirely.
    for (let i = 0; i < state.cars.length; i++) {
      for (let j = i + 1; j < state.cars.length; j++) {
        const a = state.cars[i]!;
        const b = state.cars[j]!;
        if (a.eliminated || b.eliminated) continue;
        pushApart(a, b, tuning.radius * 2);
      }
    }
  }

  advancePhase(track, state, cfg, DT);
}
